import { asArray, isShallowEqual, makeIterator } from '@/utils/data';
import { toBoolean, toNumber } from '@/utils/string';
import { createRouter, createWebHistory, isNavigationFailure, } from 'vue-router';
import { reportService } from '@/core';
import History from '@/core/router/history';
import { globalRoutes, emailConfirmRoutes, protectedRoutes } from '@/routes.ts';
export default class Router {
    /**
     * Router constructor.
     */
    constructor() {
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
        this._router = createRouter({
            history: createWebHistory(),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            // savedPosition type is not exported from vue-router
            scrollBehavior(routeTo, routeFrom, savedPosition) {
                var _a, _b, _c;
                if (savedPosition) {
                    return savedPosition;
                }
                let shouldReset = null;
                // move to top if changing route
                if ((routeTo === null || routeTo === void 0 ? void 0 : routeTo.name) !== (routeFrom === null || routeFrom === void 0 ? void 0 : routeFrom.name)) {
                    return { top: 0 };
                }
                if (((_a = routeTo.meta) === null || _a === void 0 ? void 0 : _a.scroll) && routeTo.meta.scroll.ignoreScrollToTop) {
                    return null;
                }
                if (((_b = routeTo.meta) === null || _b === void 0 ? void 0 : _b.scroll) && routeTo.name === (routeFrom === null || routeFrom === void 0 ? void 0 : routeFrom.name)) {
                    if (routeTo.meta.scroll.ignoreQuery) {
                        const toQuery = Object.assign({}, routeTo.query);
                        const fromQuery = Object.assign({}, routeFrom.query);
                        let ignoredChanged = false;
                        routeTo.meta.scroll.ignoreQuery.forEach(it => {
                            if (toQuery[it] !== fromQuery[it]) {
                                ignoredChanged = true;
                            }
                            delete toQuery[it];
                            delete fromQuery[it];
                        });
                        shouldReset = !(isShallowEqual(toQuery, fromQuery) && ignoredChanged);
                    }
                }
                if (!shouldReset && ((_c = routeTo.meta) === null || _c === void 0 ? void 0 : _c.scroll) && routeTo.name === (routeFrom === null || routeFrom === void 0 ? void 0 : routeFrom.name)) {
                    if (routeTo.meta.scroll.ignoreParams) {
                        const toParams = Object.assign({}, routeTo.params);
                        const fromParams = Object.assign({}, routeFrom.params);
                        let ignoredChanged = false;
                        routeTo.meta.scroll.ignoreParams.forEach(it => {
                            if (toParams[it] !== fromParams[it]) {
                                ignoredChanged = true;
                            }
                            delete toParams[it];
                            delete fromParams[it];
                        });
                        shouldReset = !(isShallowEqual(toParams, fromParams) && ignoredChanged);
                    }
                }
                if (!shouldReset) {
                    return null;
                }
                return { top: 0 };
            },
            routes: [
                { path: '/:pathMatch(.*)*', component: () => import('@/pages/empty.vue') },
                ...globalRoutes(),
                ...protectedRoutes(),
                ...emailConfirmRoutes(),
            ],
        });
        this.history = new History(this._router);
        this._tracking = true;
    }
    /**
     * Get current route.
     */
    get current() {
        return this._router.currentRoute;
    }
    afterEach(guard) {
        this._router.afterEach(guard);
    }
    /**
     * Add before each router guard.
     * @param {*} guard
     */
    beforeEach(guard) {
        this._router.beforeEach(guard);
    }
    onError(handler) {
        this._router.onError(handler);
    }
    /**
     * Go to new page.
     */
    push(config, track = true) {
        this._tracking = track;
        this._router.push(config)
            .then(() => this.history.resetTracking())
            .catch((err) => {
            if (isNavigationFailure(err)) {
                console.warn(err);
            }
            else {
                console.error(err);
                reportService.report(err);
            }
            this.history.resetTracking();
        });
    }
    /**
     * Replace current page.
     */
    replace(config) {
        this._tracking = false;
        this._router.replace(config)
            .then(() => this.history.resetTracking())
            .catch((err) => {
            if (isNavigationFailure(err)) {
                console.warn(err);
            }
            else {
                console.error(err);
                reportService.report(err);
            }
            this.history.resetTracking();
        });
    }
    /**
     * Update query object.
     */
    updateQuery(map, replace = false) {
        const query = Object.assign({}, this._router.currentRoute.value.query);
        for (const { key: param, value } of makeIterator(map)) {
            if (['', null, undefined].indexOf(value) === -1) {
                query[param] = value;
            }
            else {
                delete query[param];
            }
        }
        if (replace) {
            this.replace({ query });
        }
        else {
            this.push({ query });
        }
    }
    /**
     * Get query param or all query params.
     */
    input(name = null, def = null, type = null) {
        const route = this.current;
        const query = route ? route.value.query : {};
        if (!name) {
            return Object.assign({}, query);
        }
        if (!query[name]) {
            return def;
        }
        if (type === null) {
            return query[name];
        }
        const value = query[name];
        if (type === Number) {
            return toNumber(value, def);
        }
        else if (type === Boolean) {
            return toBoolean(value, def);
        }
        else if (type === Array) {
            return asArray(value);
        }
        return def;
    }
    /**
     * Go back to last unique route.
     */
    backToUnique(defaultConfig = null) {
        if (this.history.lastUnique) {
            this._router.push(this.history.lastUnique);
        }
        else {
            if (defaultConfig) {
                this._router.push(defaultConfig);
            }
            else {
                this._router.back();
            }
        }
    }
}
